import React, { useState } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Select,
  useToast,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useStores } from '../../store/store_context';
import RolesSearch from '../roles_autocomplete';

// Валидационная схема для Formik и Yup
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Обязательное поле'),
  username: Yup.string().required('Обязательное поле'),
  hashed_password: Yup.string().required('Обязательное поле'),
  description: Yup.string(),
  phone: Yup.string().required('Обязательное поле'),
  language: Yup.string().required('Обязательное поле'),
  customer_type: Yup.string().required('Обязательное поле'),
  contract_number: Yup.string().required('Обязательное поле'),
});

// Генерация случайного 16-значного номера договора
const generateContractNumber = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 16; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

// Проверка уникальности номера договора
const isContractNumberUnique = async (contractNumber, token) => {
  try {
    const response = await fetch('https://admin.i-panel.pro:8787/api/users/get_all_users', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const users = await response.json();
    return !users.some(user => user.contract_number === contractNumber);
  } catch (error) {
    console.error('Ошибка при проверке уникальности номера контракта:', error);
    return false;
  }
};

// Компонент формы для создания нового пользователя
const UserCreateForm = ({
  initialValues,
  onSubmit,
  updateUsersTable,
  rolesForUserForm,
  changeTab,
}) => {
  const [roleId, setRoleId] = useState(1);
  const { pageStore } = useStores();
  const toast = useToast();

  const handleGenerateAndCheckContractNumber = async setFieldValue => {
    let contractNumber;
    let isUnique = false;

    while (!isUnique) {
      contractNumber = generateContractNumber();
      isUnique = await isContractNumberUnique(contractNumber, pageStore.token);
    }

    setFieldValue('contract_number', contractNumber);
    toast({
      title: 'Номер контракта сгенерирован и установлен.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSubmit = async (values, actions) => {
    const restructeredValues = { ...values, role_id: roleId };
    try {
      await onSubmit(restructeredValues, pageStore.token);
      toast({
        title: 'Пользователь успешно создан.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Ошибка при отправке данных:', error);
      toast({
        title: 'Ошибка',
        description: 'Что-то пошло не так. Пожалуйста, попробуйте снова.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      await updateUsersTable();
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, setFieldValue }) => (
        <Form>
          <Field name="name">
            {({ field }) => (
              <FormControl isInvalid={errors.name && touched.name}>
                <FormLabel htmlFor="name">ФИО:</FormLabel>
                <Input {...field} id="name" />
              </FormControl>
            )}
          </Field>

          <Field name="username">
            {({ field }) => (
              <FormControl isInvalid={errors.username && touched.username}>
                <FormLabel htmlFor="username">Никнейм:</FormLabel>
                <Input {...field} id="username" />
              </FormControl>
            )}
          </Field>

          <Field name="hashed_password">
            {({ field }) => (
              <FormControl
                isInvalid={errors.hashed_password && touched.hashed_password}
              >
                <FormLabel htmlFor="hashed_password">Пароль:</FormLabel>
                <Input {...field} id="hashed_password" type="password" />
              </FormControl>
            )}
          </Field>

          <Field name="description">
            {({ field }) => (
              <FormControl
                isInvalid={errors.description && touched.description}
              >
                <FormLabel htmlFor="description">Краткое описание:</FormLabel>
                <Textarea {...field} id="description" />
              </FormControl>
            )}
          </Field>

          <Field name="phone">
            {({ field }) => (
              <FormControl isInvalid={errors.phone && touched.phone}>
                <FormLabel htmlFor="phone">Телефон:</FormLabel>
                <Input {...field} id="phone" />
              </FormControl>
            )}
          </Field>

          <RolesSearch
            items={rolesForUserForm.map(elem => {
              return { id: elem.role_id, name: elem.role_name };
            })}
            setResult={setRoleId}
          />

          <Field name="language">
            {({ field }) => (
              <FormControl isInvalid={errors.language && touched.language}>
                <FormLabel htmlFor="language">Язык:</FormLabel>
                <Input {...field} id="language" />
              </FormControl>
            )}
          </Field>

          <Field name="customer_type">
            {({ field }) => (
              <FormControl
                isInvalid={errors.customer_type && touched.customer_type}
              >
                <FormLabel htmlFor="customer_type">Тип пользователя:</FormLabel>
                <Select {...field} id="customer_type">
                  <option value="УК" style={{ color: 'black' }}>
                    УК
                  </option>
                  <option value="ТСЖ" style={{ color: 'black' }}>
                    ТСЖ
                  </option>
                  <option value="ЖСК" style={{ color: 'black' }}>
                    ЖСК
                  </option>
                  <option value="БЦ" style={{ color: 'black' }}>
                    БЦ
                  </option>
                  <option value="ТЦ" style={{ color: 'black' }}>
                    ТЦ
                  </option>
                  <option value="Бизнес Партнер" style={{ color: 'black' }}>
                    Бизнес Партнер
                  </option>
                  <option value="МСК" style={{ color: 'black' }}>
                    МСК
                  </option>
                  <option
                    value="Региональный Партнер"
                    style={{ color: 'black' }}
                  >
                    Региональный Партнер
                  </option>
                </Select>
              </FormControl>
            )}
          </Field>

          <Field name="contract_number">
            {({ field }) => (
              <FormControl
                isInvalid={errors.contract_number && touched.contract_number}
              >
                <FormLabel htmlFor="contract_number">
                  Номер контракта:
                </FormLabel>
                <Text fontSize={10} color={'gray'}>
                  *генерируйте номер только в случае если вы являетесь главным
                  администратором и добавляете первого пользователя заказчика
                </Text>
                <InputGroup>
                  <Input {...field} id="contract_number" />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      fontSize={8}
                      onClick={() =>
                        handleGenerateAndCheckContractNumber(setFieldValue)
                      }
                    >
                      Сгенерировать
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            )}
          </Field>

          <Button
            mt={4}
            type="submit"
            backgroundColor={'#7B79FF'}
            _hover={{ backgroundColor: '#4945FF' }}
            color="white"
            onClick={() => changeTab(2)}
          >
            Сохранить
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default UserCreateForm;
